import { ArrowLeft, Inbox, Upload as UploadIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";
import { ApiError, portal, type CorrespondenceUpload } from "../lib/api";

function prettySourceHint(s: string | null | undefined): string {
  if (!s) return "Document";
  const map: Record<string, string> = {
    equifax: "Letter from Equifax",
    experian: "Letter from Experian",
    transunion: "Letter from TransUnion",
    original_creditor: "Letter from a creditor",
    debt_collector: "Letter from a debt collector",
    email_screenshot: "Screenshot of an email",
    unknown: "Document",
  };
  return map[s] || s.replace(/_/g, " ");
}

export default function PortalHistoryPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<CorrespondenceUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await portal.listCorrespondence();
        setItems(list);
      } catch (e: any) {
        if (e instanceof ApiError && e.status === 401) {
          nav("/portal/login", { replace: true });
          return;
        }
        setErr(e?.message || "Couldn't load your uploads.");
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  return (
    <PortalShell>
      <section className="portal-section">
        <p className="portal-eyebrow">Upload history</p>
        <h1 className="portal-headline">Your timeline.</h1>
        <p className="portal-sub">
          Every letter and screenshot you've sent us. Once a specialist reviews
          one, you'll see it marked as <em>Reviewed</em>.
        </p>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 4,
          }}
        >
          <Link to="/portal/upload" className="portal-btn-primary">
            <UploadIcon size={16} aria-hidden="true" /> Upload another
          </Link>
          <Link to="/portal/dashboard" className="portal-btn-ghost">
            <ArrowLeft size={14} aria-hidden="true" /> Back to dashboard
          </Link>
        </div>
      </section>

      <article className="portal-card">
        <div className="portal-card-heading">
          <h2>Activity</h2>
        </div>

        {loading && <p className="portal-empty">Loading your activity…</p>}
        {err && <p className="portal-error">{err}</p>}

        {!loading && !err && items.length === 0 && (
          <p className="portal-empty">
            <Inbox size={15} aria-hidden="true" style={{ verticalAlign: "-2px", marginRight: 6, opacity: 0.6 }} />
            You haven't uploaded any correspondence yet. When you do, it'll show
            up here.
          </p>
        )}

        {items.length > 0 && (
          <ol className="portal-timeline" aria-label="Upload activity timeline">
            {items.map((c) => {
              const title = prettySourceHint(c.source_hint);
              const uploadedAt = new Date(c.uploaded_at);
              const uploadedAtLabel = uploadedAt.toLocaleString();
              const statusLabel = c.analyzed
                ? "Reviewed"
                : "Waiting for review";
              return (
                <li
                  className={`portal-timeline-event${c.analyzed ? "" : " muted"}`}
                  key={c.id}
                  aria-label={`${title}, uploaded ${uploadedAtLabel}, ${statusLabel}`}
                >
                  <h3 className="portal-timeline-title">{title}</h3>
                  <div className="portal-timeline-meta">
                    {c.original_filename || "Upload"} ·{" "}
                    <time dateTime={uploadedAt.toISOString()}>
                      {uploadedAtLabel}
                    </time>
                  </div>
                  <span
                    className={`portal-timeline-status${c.analyzed ? " reviewed" : ""}`}
                  >
                    {statusLabel}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </article>
    </PortalShell>
  );
}
